import type { BlogPost } from "../../lib/blog";

export const ideesCadeauxSecretSanta: BlogPost = {
  h1: "Idées cadeaux pour un Secret Santa : à 10, 20, 30 et 50 €",
  seoTitle: "Idées cadeaux Secret Santa : Père Noël secret à 10, 20, 30 et 50 € · Listou",
  seoDescription:
    "Des idées de cadeaux pour un Secret Santa ou Père Noël secret, par budget : 10 € au bureau, 20 € entre amis, 30 € en famille, 50 € pour remplacer tous les cadeaux. Et quoi offrir à quelqu'un qu'on connaît peu.",
  publishedOn: "2026-10-20",
  updatedOn: "2026-10-20",
  summary:
    "Le meilleur cadeau de Secret Santa est celui que la personne a écrit sur sa liste d'envies. À défaut, choisissez selon le budget : à 10 €, un cadeau gourmand ou un objet du quotidien ; à 20 €, un plaid, un livre ou un puzzle ; à 30 €, un jeu de société ou un coffret ; à 50 €, un casque, une lampe ou une expérience. Pour quelqu'un qu'on connaît peu, le gourmand et le pratique ne déçoivent jamais.",
  body: `
## Commencez par sa liste d'envies

Avant de chercher, regardez si la personne tirée a écrit ce qui lui ferait plaisir. Dans un Père Noël secret organisé sur [Listou](/secret-santa), sa liste s'ouvre avec le tirage, avec le budget en tête. Un cadeau pris dans la liste est un cadeau qui sert. Un cadeau choisi à côté peut toucher davantage, mais c'est un pari : gardez-le pour quelqu'un dont vous connaissez les goûts.

Pas de liste ? Observez. Ce qu'elle boit le matin, ce qu'elle lit dans le métro, le sport qu'elle mentionne, la plante qui meurt sur son bureau. Un cadeau qui montre qu'on a fait attention vaut plus que son prix.

## À 10 € : le petit plaisir

Le budget du bureau et des grands groupes. Visez l'objet qu'on ne s'achète pas soi-même, mais qu'on est content d'avoir.

- **Gourmand** : une tablette de chocolat d'un artisan, un sachet de thé ou de café de torréfacteur, un pot de miel ou de confiture de producteur, un sirop pour le chocolat chaud.
- **Du quotidien** : des chaussettes de Noël, un [mug](/idees-cadeaux?q=Mug) qui change du mug d'entreprise, une bougie parfumée, un baume à lèvres de qualité.
- **Pour jouer** : un [jeu de cartes](/idees-cadeaux?q=Jeu%20de%20cartes), un petit casse-tête, un cahier de mots fléchés.
- **Pour le bureau** : un carnet, un bon stylo, une plante grasse, un porte-stylo.

## À 20 € : un vrai cadeau

Le budget le plus courant entre amis. Il permet un objet qui reste.

- **Confort** : un [plaid](/idees-cadeaux?q=Plaid), des pantoufles, une bouillotte, une tisane d'hiver avec sa tasse.
- **À lire ou à faire** : un [livre de cuisine](/idees-cadeaux?q=Livre%20de%20cuisine), un roman dont tout le monde parle, un [puzzle de mille pièces](/idees-cadeaux?q=Puzzle), un kit de broderie.
- **Vert** : une [plante d'intérieur](/idees-cadeaux?q=Plante) avec son cache-pot, un kit de pousses de champignons, un herbier à monter.
- **Gourmand encore** : un coffret de biscuits, une huile d'olive de domaine, une bouteille de vin ou de bière locale pour qui en boit.

## À 30 € : le cadeau de famille

Le bon compromis quand le Père Noël secret remplace les cadeaux entre cousins ou entre frères et sœurs.

- **Pour jouer ensemble** : un [jeu de société](/idees-cadeaux?q=Jeu%20de%20société) pour les soirées, un jeu d'ambiance à huit, un jeu de cartes à deux.
- **À porter** : un [pull de Noël](/idees-cadeaux?q=Pull%20de%20Noël), un bonnet ou une écharpe en laine, un pyjama en flanelle.
- **Coffrets** : un [coffret de thé](/idees-cadeaux?q=Coffret%20thé), de chocolats, d'épices, ou de dégustation.
- **Pour la maison** : une lampe d'appoint, un diffuseur, une belle planche à découper, un plateau de service.

## À 50 € et plus : un seul cadeau, mais un beau

Pour une famille qui remplace tous les cadeaux par un seul. On peut viser l'objet que la personne repousse d'acheter depuis des mois.

- **Tech** : un [casque audio](/idees-cadeaux?q=Casque%20audio), une [enceinte Bluetooth](/idees-cadeaux?q=Enceinte), une liseuse, un chargeur sans fil.
- **Expérience** : une [carte cadeau expérience](/idees-cadeaux?q=Carte%20cadeau), un atelier de cuisine ou de céramique, une place de concert, un massage.
- **Design** : une [lampe](/idees-cadeaux?q=Lampe), un vase, une affiche encadrée, un objet d'un créateur local.
- **Gourmand haut de gamme** : un [coffret de dégustation](/idees-cadeaux?q=Coffret%20dégustation), une cave à vin de six bouteilles, un abonnement à une box de café.

## Pour quelqu'un qu'on connaît peu

C'est le cas du collègue d'un autre service, ou du nouveau conjoint d'un cousin. Trois règles.

- **Le gourmand ne déçoit pas**, à condition d'éviter l'alcool si vous ne savez pas que la personne en boit.
- **Le pratique rassure** : un bon carnet, une gourde, une lampe de lecture, des chaussettes chaudes. On s'en sert, même sans y avoir pensé.
- **L'expérience ouverte laisse le choix** : une carte cadeau d'une librairie, d'un cinéma ou d'un restaurant que la personne aime.

Évitez le parfum, les cosmétiques et les vêtements, trop personnels, le cadeau « blague » qui ne fait rire que celui qui l'offre, et tout ce qui touche à la religion, à la politique ou au physique.

## Les cadeaux qui finissent au fond du placard

- **La bougie parfumée par défaut**, celle qu'on achète la veille parce qu'on n'a pas lu la liste.
- **Le gadget inutile**, qui amuse trente secondes.
- **Le cadeau qui impose un goût** : un livre de développement personnel, un ustensile de régime, un objet de décoration qui ne va avec rien.
- **Le cadeau hors budget**, trop cher ou trop bon marché, qui met tout le monde mal à l'aise.

## Un cadeau d'occasion, fait main, ou local ?

Ce sont trois bonnes réponses au budget serré, et trois thèmes possibles pour tout le groupe. Un livre de seconde main en bon état, un pot de confiture maison étiqueté à la main, un objet acheté au marché de Noël de la ville : à 10 €, ils valent souvent plus qu'un objet neuf.

Pour les règles du jeu et le budget à fixer, lisez notre [guide du Père Noël secret](/blog/pere-noel-secret-regles-budget). Pour que chacun sache quoi offrir, le [Secret Santa de Listou](/secret-santa) relie le tirage à la liste d'envies de la personne tirée.
`,
  faq: [
    [
      "Quel est le meilleur cadeau pour un Secret Santa ?",
      "Celui que la personne a écrit sur sa liste d'envies. Sans liste, un cadeau gourmand d'artisan ou un objet pratique et de qualité font plaisir à tout le monde.",
    ],
    [
      "Quoi offrir pour un Secret Santa à 10 € ?",
      "Une tablette de chocolat d'artisan, un thé ou un café de torréfacteur, des chaussettes de Noël, un mug, une bougie, un jeu de cartes, une plante grasse ou un carnet.",
    ],
    [
      "Quoi offrir pour un Secret Santa à 20 € ?",
      "Un plaid, des pantoufles, un livre de cuisine, un puzzle de mille pièces, une plante d'intérieur avec son cache-pot, un coffret de biscuits ou une bouteille locale.",
    ],
    [
      "Quoi offrir pour un Secret Santa à 30 € ?",
      "Un jeu de société, un pull de Noël, un bonnet en laine, un coffret de thé ou de chocolats, une lampe d'appoint ou une belle planche à découper.",
    ],
    [
      "Quoi offrir à un collègue qu'on connaît peu ?",
      "Du gourmand sans alcool, un objet pratique comme un carnet ou une gourde, ou une carte cadeau d'une librairie ou d'un cinéma. Évitez le parfum, les vêtements et les cadeaux blague.",
    ],
    [
      "Peut-on offrir un cadeau d'occasion ou fait main ?",
      "Oui, et c'est souvent le meilleur cadeau à petit budget. Annoncez-le comme règle commune si vous voulez que tout le groupe joue le jeu.",
    ],
  ],
  cta: "secret_santa",
};
